import { Drawer, DrawerHeader, DrawerItems, Dropdown, DropdownDivider, DropdownItem, Tooltip } from 'flowbite-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
   HiOutlineChevronDown,
   HiOutlineChevronRight,
   HiOutlineGlobeAlt,
   HiOutlineHeart,
   HiOutlineLogout,
   HiOutlineMap,
   HiOutlineMenu,
   HiOutlineOfficeBuilding,
   HiOutlineUserCircle,
   HiOutlineX,
} from 'react-icons/hi';
import { HiOutlineUsers } from 'react-icons/hi2';
import { RiUserSettingsLine } from 'react-icons/ri';
import { NavLink, useLocation, useNavigate } from 'react-router';
import Avatar from 'src/components/avatar/Avatar';
import Button from 'src/components/button/button';
import TruncateTooltip from 'src/components/TruncateTooltip';
import { useAuth } from 'src/contexts/AuthContext';
import { useOrganization } from 'src/contexts/OrganizationContext';
import { routePaths } from 'src/utils/constants';
import { getFullname } from 'src/utils/string.util';
import { twMerge } from 'tailwind-merge';

const drawerTheme = {
   header: {
      inner: {
         closeIcon: 'w-5 h-5',
         titleIcon: 'hidden',
      },
   },
};

const dropdownTheme = {
   content: 'py-0',
   floating: {
      base: 'border-none rounded-10 shadow-md',
      item: {
         base: 'px-3.75 py-3.5 hover:bg-bgNavInteract min-w-60',
      },
   },
};

const renderDrawerButton = (
   Icon: React.ElementType,
   label: string,
   hasArrow: boolean,
   variant?: 'primary' | 'secondary',
   className?: string,
   onClick?: () => void,
) => {
   const classes =
      variant === 'primary'
         ? 'bg-primary text-white active:text-white active:border-tertiary active:bg-tertiary '
         : 'bg-white text-black border border-solid border-border active:text-white active:border-primary active:bg-primary ';

   return (
      <a
         href="#"
         className={twMerge(
            `${classes} flex items-center p-3.75 w-full rounded-md h-12 !text-l-sm font-semibold [&:not(:first-child)]:mt-4 group`,
            className,
         )}
         onClick={e => {
            e.preventDefault();
            e.stopPropagation();

            onClick?.();
         }}
      >
         <Icon />
         <span className="flex-1 ms-3 whitespace-nowrap">{label}</span>
         {hasArrow && <HiOutlineChevronRight size={14} color="text-twGray" />}
      </a>
   );
};

const UserProfileMenu = (props: any) => {
   const { organization: selectedOrganization, darkMode } = props;
   const { organization } = useOrganization();

   const [t] = useTranslation();
   const navigate = useNavigate();
   const location = useLocation();

   const { user, isLoading, isAdmin, isAuthenticated, isOrgAdmin, isDisabled, signIn, signUp } = useAuth();

   const fullName = getFullname(user?.firstName, user?.lastName);

   const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
   const [authActionPending, setAuthActionPending] = useState<'signIn' | 'signUp' | null>(null);

   const isAdminRoute = location.pathname?.indexOf(routePaths.ADMIN.INDEX) >= 0;

   useEffect(() => {
      if (isMobileNavOpen) {
         document.body.style.overflow = 'hidden';
      } else {
         document.body.style.overflow = '';
      }

      return () => {
         document.body.style.overflow = '';
      };
   }, [isMobileNavOpen]);

   return (
      <>
         {/* Desktop menus */}
         {!isLoading && user?.id && (
            <div className="hidden md:flex items-center">
               <Dropdown
                  inline
                  label={
                     <div className="flex items-center gap-1">
                        <Avatar user={user} />
                        <HiOutlineChevronDown color={`'black`} size={8} className={`${darkMode === true ? 'stroke-white' : ''}`} />
                     </div>
                  }
                  theme={dropdownTheme}
                  arrowIcon={false}
               >
                  <div className="p-3.5 flex flex-1 gap-2.5 max-w-[250px]">
                     <Avatar user={user} />
                     <p className="flex flex-col wrap justify-center items-start gap-1.25 mb-0 min-w-0 overflow-hidden">
                        {fullName !== '' && (
                           <Tooltip content={String(fullName ?? '')} trigger="hover" style="dark">
                              <span className="w-full text-sm font-semibold line-clamp-2 break-all">{fullName}</span>
                           </Tooltip>
                        )}
                        <TruncateTooltip text={String(user?.emailAddress ?? '')}>
                           <span className="w-full text-xs text-twGray font-medium">{user?.emailAddress}</span>
                        </TruncateTooltip>
                     </p>
                  </div>
                  <DropdownDivider className="my-0 bg-border" />
                  {isAdminRoute && (
                     <DropdownItem
                        icon={() => <HiOutlineGlobeAlt size={18} color="#686868" className="mr-2.5" />}
                        onClick={() => navigate(routePaths.LANDING)}
                     >
                        <div className="flex flex-1 items-center justify-between">
                           <span className="font-semibold text-sm text-black">{t('navbar.landing')}</span>
                           <HiOutlineChevronRight size={12} color="#686868" />
                        </div>
                     </DropdownItem>
                  )}

                  {isAdmin && !isAdminRoute && (
                     <DropdownItem
                        icon={() => <HiOutlineUsers size={18} color="#686868" className="mr-2.5" strokeWidth={2} />}
                        onClick={() => navigate(routePaths.ADMIN.ORGANIZATION.INDEX)}
                     >
                        <div className="flex flex-1 items-center justify-between">
                           <span className="font-semibold text-sm text-black">{t('navbar.admin')}</span>
                           <HiOutlineChevronRight size={12} color="#686868" />
                        </div>
                     </DropdownItem>
                  )}

                  <DropdownItem
                     icon={() => <HiOutlineUserCircle size={18} color="#686868" className="mr-2.5" />}
                     onClick={() => navigate(routePaths.MY_ACCOUNT)}
                  >
                     <div className="flex flex-1 items-center justify-between">
                        <span className="font-semibold text-sm text-black">{t('navbar.myAccount')}</span>
                        <HiOutlineChevronRight size={12} color="#686868" />
                     </div>
                  </DropdownItem>
                  {isAuthenticated && (
                     <DropdownItem
                        icon={() => <HiOutlineHeart size={18} color="#686868" className="mr-2.5" />}
                        onClick={() => {
                           sessionStorage.setItem('prevPath', location.pathname);
                           sessionStorage.setItem('prevParams', location.search);
                           navigate(routePaths.MY_FAVORITES);
                        }}
                     >
                        <div className="flex flex-1 items-center justify-between">
                           <span className="font-semibold text-sm text-black">{t('navbar.myFavorites')}</span>
                           <HiOutlineChevronRight size={12} color="#686868" />
                        </div>
                     </DropdownItem>
                  )}
                  {isAuthenticated && (
                     <DropdownItem
                        icon={() => <HiOutlineMap size={18} color="#686868" className="mr-2.5" />}
                        onClick={() => navigate(routePaths.MY_TRIPS)}
                     >
                        <div className="flex flex-1 items-center justify-between">
                           <span className="font-semibold text-sm text-black">{t('navbar.myTrips')}</span>
                           <HiOutlineChevronRight size={12} color="#686868" />
                        </div>
                     </DropdownItem>
                  )}

                  {!isAdmin && isOrgAdmin && (
                     <DropdownItem
                        icon={() => <HiOutlineOfficeBuilding size={18} color="#686868" className="mr-2.5" />}
                        onClick={() => navigate(routePaths.ADMIN.ORGANIZATION.EDIT.replace(':id', organization?.id || ''))}
                     >
                        <div className="flex flex-1 items-center justify-between">
                           <span className="font-semibold text-sm text-black">{t('sidebar.organizationSettings')}</span>
                           <HiOutlineChevronRight size={12} color="#686868" />
                        </div>
                     </DropdownItem>
                  )}

                  <DropdownItem
                     icon={() => <HiOutlineUserCircle size={18} color="#686868" className="mr-2.5" />}
                     onClick={() => window.open('/faq', '_blank')}
                  >
                     <div className="flex flex-1 items-center justify-between">
                        <span className="font-semibold text-sm text-black">FAQ</span>
                        <HiOutlineChevronRight size={12} color="#686868" />
                     </div>
                  </DropdownItem>

                  <DropdownDivider className="my-0 bg-border" />
                  <DropdownItem
                     className="rounded-b-10"
                     icon={() => <HiOutlineLogout size={18} color="#E02424" className="mr-2.5" />}
                     onClick={() => (window.location.href = routePaths.LOGOUT_PAGE)}
                  >
                     <div className="flex flex-1 items-center justify-between">
                        <span className="font-semibold text-sm text-twRed">{t('header.signOut')}</span>
                     </div>
                  </DropdownItem>
               </Dropdown>
            </div>
         )}

         {(!isLoading || !!authActionPending) && !user?.id && !isDisabled && (
            <div className="flex gap-2 sm:gap-4">
               <Button
                  className="h-8 px-3 text-xs sm:text-sm"
                  onClick={async () => {
                     if (authActionPending) return;

                     setAuthActionPending('signUp');
                     try {
                        await signUp({ handoff: { trigger: 'header' } });
                     } catch {
                        setAuthActionPending(null);
                     }
                  }}
                  disabled={!!authActionPending}
                  outline
                  org={!!selectedOrganization?.color}
               >
                  {authActionPending === 'signUp' ? (
                     <span className="flex items-center gap-1.5">
                        <span className="inline-block h-3 w-3 rounded-full border-2 border-current border-r-transparent animate-spin" />
                        {t('header.redirecting')}
                     </span>
                  ) : (
                     t('header.register')
                  )}
               </Button>
               <Button
                  className="h-8 px-3 text-xs sm:text-sm"
                  onClick={async () => {
                     if (authActionPending) return;

                     setAuthActionPending('signIn');
                     try {
                        await signIn({ handoff: { trigger: 'header' } });
                     } catch {
                        setAuthActionPending(null);
                     }
                  }}
                  disabled={!!authActionPending}
                  outline={!selectedOrganization?.color}
                  org={!!selectedOrganization?.color}
               >
                  {authActionPending === 'signIn' ? (
                     <span className="flex items-center gap-1.5">
                        <span className="inline-block h-3 w-3 rounded-full border-2 border-current border-r-transparent animate-spin" />
                        {t('header.redirecting')}
                     </span>
                  ) : (
                     t('header.signIn')
                  )}
               </Button>
            </div>
         )}

         {/* Mobile burger icon - only show for authenticated users */}
         {!isLoading && user?.id && (
            <button
               aria-label="Open navigation menu"
               className="flex md:hidden w-6 h-6 justify-center items-center rounded-md active:bg-border"
               onClick={() => setIsMobileNavOpen(true)}
            >
               <HiOutlineMenu className="w-6 h-6" color={darkMode ? 'white' : 'black'} />
            </button>
         )}

         {/* Mobile menus */}
         <Drawer
            className={`!bg-white px-6 w-full h-full z-[999] fixed inset-0 overflow-auto ${isMobileNavOpen ? 'md:hidden 2xs:block' : 'hidden'}`}
            open={isMobileNavOpen}
            onClose={() => setIsMobileNavOpen(false)}
            theme={drawerTheme}
         >
            <DrawerHeader titleIcon={undefined} closeIcon={() => <HiOutlineX size={20} color="black" />} />
            <DrawerItems>
               {/* User section */}
               {user?.id && (
                  <div className="flex justify-center rounded-full p-4">
                     <div className="text-center">
                        <div className="flex items-center justify-center">
                           <Avatar
                              user={user}
                              theme={{
                                 root: {
                                    rounded: 'min-w-15 min-h-15 rounded-full',
                                    initials: {
                                       text: 'font-semibold xs:text-lg text-black',
                                    },
                                 },
                              }}
                           />
                        </div>
                        <p className="mt-2.5 mb-0">
                           <span className="font-bold text-l-lg">{fullName}</span>
                        </p>
                        <p className="mb-0">
                           <span className="text-l-sm text-twGray">{user?.emailAddress}</span>
                        </p>
                     </div>
                  </div>
               )}

               <div className="mt-5">
                  {/* Logged in user */}
                  {user?.id && (
                     <>
                        {isAdminRoute && !isOrgAdmin && (
                           <div
                              className={'flex justify-center items-center w-full rounded-md h-12 bg-bgNavInteract'}
                              onClick={e => {
                                 e.preventDefault();
                                 e.stopPropagation();
                              }}
                           >
                              <div className="flex items-center flex-nowrap gap-x-1">
                                 <RiUserSettingsLine className="block" size={22} color="#62A4AB" />
                                 <span className="w-full block text-primary text-sm font-semibold whitespace-nowrap">{t('navbar.admin')}</span>
                              </div>
                           </div>
                        )}

                        {renderDrawerButton(
                           () => (
                              <HiOutlineUserCircle size={18} color="#686868" className="group-active:stroke-white group-[.active]:stroke-white" />
                           ),
                           t('navbar.myAccount'),
                           true,
                           'secondary',
                           '',
                           () => {
                              setIsMobileNavOpen(false);

                              navigate(routePaths.MY_ACCOUNT);
                           },
                        )}

                        {renderDrawerButton(
                           () => (
                              <HiOutlineMap size={18} color="#686868" className="group-active:stroke-white group-[.active]:stroke-white" />
                           ),
                           t('navbar.myTrips'),
                           true,
                           'secondary',
                           '',
                           () => {
                              setIsMobileNavOpen(false);

                              navigate(routePaths.MY_TRIPS);
                           },
                        )}

                        {renderDrawerButton(
                           () => (
                              <HiOutlineHeart size={18} color="#686868" className="group-active:stroke-white group-[.active]:stroke-white" />
                           ),
                           t('navbar.myFavorites'),
                           true,
                           'secondary',
                           '',
                           () => {
                              setIsMobileNavOpen(false);
                              sessionStorage.setItem('prevPath', location.pathname);
                              sessionStorage.setItem('prevParams', location.search);
                              navigate(routePaths.MY_FAVORITES);
                           },
                        )}

                        {/* Admin section */}
                        {isAdmin && (
                           <div className="border rounded-6 mt-3.75">
                              <a
                                 className={
                                    'flex border-b items-center p-3.75 w-full rounded-t-md h-12 !text-l-sm font-semibold [&:not(:first-child)]:mt-4 disabled'
                                 }
                              >
                                 <RiUserSettingsLine color="#686868" size={18} />
                                 <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.siteAdmin')}</span>
                              </a>
                              <div>
                                 <NavLink
                                    to={routePaths.ADMIN.ORGANIZATION.INDEX}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.organizations')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>
                                 <NavLink
                                    to={routePaths.ADMIN.MANAGE_TRIP.INDEX}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.manageTrips')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>
                                 <NavLink
                                    to={routePaths.ADMIN.USER.INDEX}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.users')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>
                                 <NavLink
                                    to={routePaths.ADMIN.TRANSACTIONS.INDEX}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.transactions')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>
                                 <NavLink
                                    to={routePaths.ADMIN.COUPON.INDEX}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.coupon')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>
                                 <NavLink
                                    to={routePaths.ADMIN.REVIEW.INDEX}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.review')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>
                                 <NavLink
                                    to={routePaths.ADMIN.HOTEL_BLACKLIST.INDEX}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.hotelBlacklist')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>

                                 <NavLink
                                    to={routePaths.ADMIN.SITE_CONFIG_REGISTRY}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.siteConfigRegistry')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>

                                 <NavLink
                                    to={routePaths.ADMIN.ACTIVITY_LOGS}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.activityLogs')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>

                                 <NavLink
                                    to={routePaths.ADMIN.BOOKING_FAILURE_TRACKER.INDEX}
                                    className={({ isActive }) =>
                                       [
                                          'flex items-center p-3.5 w-full h-12 !text-l-sm rounded-md transition-colors',
                                          isActive ? 'font-semibold text-white bg-primary' : 'text-slate-700 hover:bg-[#62A4AB] hover:text-white',
                                       ].join(' ')
                                    }
                                    onClick={() => {
                                       setIsMobileNavOpen(false);
                                    }}
                                    end
                                 >
                                    <span className="flex-1 ms-3 whitespace-nowrap">{t('sidebar.bookingFailureTracker')}</span>
                                    <HiOutlineChevronRight size={14} color="text-twGray" />
                                 </NavLink>
                              </div>
                           </div>
                        )}

                        {/* Org section */}
                        {!isAdmin &&
                           isOrgAdmin &&
                           renderDrawerButton(
                              () => (
                                 <HiOutlineOfficeBuilding
                                    size={18}
                                    color="#686868"
                                    className="group-active:stroke-white group-[.active]:stroke-white"
                                 />
                              ),
                              t('sidebar.organizationSettings'),
                              true,
                              'secondary',
                              '',
                              () => {
                                 setIsMobileNavOpen(false);

                                 navigate(routePaths.ADMIN.ORGANIZATION.EDIT.replace(':id', organization?.id || ''));
                              },
                           )}
                     </>
                  )}

                  {user?.id && (
                     <>
                        <Button onClick={() => window.open('/faq', '_blank')} className="text-white mt-3.5 w-full h-10">
                           FAQ
                        </Button>
                        <Button onClick={() => navigate(routePaths.LOGOUT_PAGE)} className="text-white mt-3.5 w-full h-10">
                           {t('header.signOut')}
                        </Button>
                     </>
                  )}
               </div>
            </DrawerItems>
         </Drawer>
      </>
   );
};

export default UserProfileMenu;
